/**
 * The one sequence that turns a freshly proven address into a proven ACCOUNT.
 *
 * Plain TypeScript — no React. Both routes into it are adapters (`useEmailVerificationPropagation`):
 * landing on the api's marked continue URL, and noticing the flip while the tab sat open. What they
 * share is not "some code", it is an ORDER that is easy to get wrong and impossible to see wrong
 * afterwards, so it is stated once here and tested once.
 */

export interface EmailTrustRefreshPorts<TAccount> {
	/** Forces the Firebase SDK to exchange its refresh token for a new ID token. */
	forceIdTokenRefresh: () => Promise<unknown>;
	/** Re-reads `/account`, which every request carries the SDK's current token on. */
	readAccount: () => Promise<TAccount>;
	/** Projects that payload onto the app's user. */
	applyAccount: (account: TAccount) => void;
}

/**
 * Forces a token refresh, then re-reads the account, then applies it.
 *
 * <b>The order is the whole contract.</b> The api promotes an unproven address when — and only when —
 * a request presents a token whose `email_verified` claim is true, and it promotes on THAT request.
 * An account read issued before the refresh carries the claim the session was signed in with, so it
 * both answers "still unverified" and leaves the database row unpromoted, which is exactly the state
 * this feature exists to leave behind. Nothing here catches: a failed refresh or a failed read must
 * surface to the caller, which decides whether to keep watching or give up.
 */
export async function refreshEmailTrust<TAccount>(ports: EmailTrustRefreshPorts<TAccount>): Promise<void> {
	await ports.forceIdTokenRefresh();
	ports.applyAccount(await ports.readAccount());
}
